// ⚡️ ORDER DETAILS CARD: Order details card with product information, price display, and platform icons
// Extracted from ChatPanel.js for better organization and reusability

import React, { useEffect, useState } from 'react';
import { apiGet, getImageUrl } from '../../../../utils/api';

const OrderDetailsCard = ({
  conversation,
  isDarkMode,
  dispatch,
  state,
  isAdmin = false
}) => {
  const [order, setOrder] = useState(null);
  const [product, setProduct] = useState(null);

  useEffect(() => {
    let ignore = false;
    const load = async () => {
      const { order_id, product_id } = conversation || {};
      try {
        if (order_id) {
          const tryEndpoints = isAdmin ? ['/api/orders/'] : ['/api/orders/my', '/api/orders/sales'];
          for (const ep of tryEndpoints) {
            const res = await apiGet(ep);
            if (!res.ok) continue;
            const list = await res.json();
            const found = Array.isArray(list) ? list.find(o => String(o.order_id) === String(order_id)) : null;
            if (found) { if (!ignore) setOrder(found); break; }
          }
        }
      } catch {}
      try {
        if (product_id) {
          const res = await apiGet(`/api/products/${product_id}`);
          if (res.ok) {
            const data = await res.json();
            if (!ignore) setProduct(data);
          }
        }
      } catch {}
    };
    load();
    return () => { ignore = true; };
  }, [conversation?.order_id, conversation?.product_id, isAdmin]);
  // Helper function to get platform-specific styling and icons
  const getPlatformInfo = (productName) => {
    const name = productName || '';
    const lowerName = name.toLowerCase();
    
    if (lowerName.includes('tiktok')) {
      return {
        bgColor: 'bg-gradient-to-br from-black to-gray-800',
        icon: (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
          </svg>
        ),
        displayName: 'TikTok Accounts'
      };
    }
    
    if (lowerName.includes('instagram')) {
      return {
        bgColor: 'bg-gradient-to-br from-pink-500 to-purple-600',
        icon: (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
          </svg>
        ),
        displayName: 'Instagram Accounts'
      };
    }
    
    if (lowerName.includes('facebook')) {
      return {
        bgColor: 'bg-gradient-to-br from-blue-600 to-blue-700',
        icon: (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
          </svg>
        ),
        displayName: 'Facebook Pages'
      };
    }
    
    if (lowerName.includes('youtube')) {
      return {
        bgColor: 'bg-gradient-to-br from-red-500 to-red-600',
        icon: (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
          </svg>
        ),
        displayName: 'YouTube Channels'
      };
    }
    
    if (lowerName.includes('twitter')) {
      return {
        bgColor: 'bg-gradient-to-br from-blue-400 to-blue-500',
        icon: (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
          </svg>
        ),
        displayName: 'Twitter Accounts'
      };
    }
    
    if (lowerName.includes('snapchat')) {
      return {
        bgColor: 'bg-gradient-to-br from-yellow-400 to-yellow-500',
        icon: (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.174-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.402.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.357-.629-2.746-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24.009 12.017 24.009c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641.001 12.017.001z"/>
          </svg>
        ),
        displayName: 'Snapchat Accounts'
      };
    }
    
    // Default fallback
    return {
      bgColor: 'bg-gradient-to-br from-purple-500 to-purple-600',
      icon: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
        </svg>
      ),
      displayName: name.length > 20 ? name.substring(0, 20) + '...' : name
    };
  };

  const platformInfo = getPlatformInfo(order?.product_title || conversation.product_name);

  return (
    <div className={`px-4 py-4 mb-3 rounded-xl shadow-sm grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 transition-all duration-300 overflow-hidden border ${
      isDarkMode 
        ? 'bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border-gray-700/50' 
        : 'bg-gradient-to-br from-gray-50 to-white border-gray-200'
    } ${state.collapsedBoxes.orderDetails ? 'max-h-0 py-0 opacity-0' : 'max-h-96 opacity-100'}`}>
      
      {/* Order ID */}
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-sm">
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>
        </span>
        <div>
          <div className={`text-xs ${
            isDarkMode ? 'text-gray-400' : 'text-gray-500'
          }`}>Order ID</div>
          <div className={`font-semibold text-sm ${
            isDarkMode ? 'text-white' : 'text-gray-800'
          }`}>{conversation.order_id || 'N/A'}</div>
        </div>
      </div>

      {/* Product Name with Platform Icon */}
      <div className="flex items-center gap-2">
        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg text-white shadow-sm ${platformInfo.bgColor}`}>
          {platformInfo.icon}
        </span>
        <div className="flex-1 min-w-0">
          <div className={`text-xs ${
            isDarkMode ? 'text-gray-400' : 'text-gray-500'
          }`}>Product</div>
          <div className={`font-semibold text-sm leading-tight ${
            isDarkMode ? 'text-white' : 'text-gray-800'
          }`} title={(order?.product_title || conversation.product_name || 'N/A')}>
            {platformInfo.displayName}
          </div>
        </div>
      </div>

      {/* Payment Status */}
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-green-500 to-green-600 text-white shadow-sm">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
            <path d="M8 12l2 2 4-4" stroke="currentColor" strokeWidth="2"/>
          </svg>
        </span>
        <div>
          <div className={`text-xs ${
            isDarkMode ? 'text-gray-400' : 'text-gray-500'
          }`}>Payment</div>
          <span className="inline-flex items-center gap-1 text-green-600 font-semibold text-sm">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="12" fill="#22c55e"/>
              <path d="M8 12l2 2 4-4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Paid
          </span>
        </div>
      </div>

      {/* Amount */}
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-gray-500 to-gray-600 text-white shadow-sm">
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </span>
        <div>
          <div className={`text-xs ${
            isDarkMode ? 'text-gray-400' : 'text-gray-500'
          }`}>Amount</div>
          <span className={`font-semibold text-sm ${
            isDarkMode ? 'text-white' : 'text-gray-800'
          }`}>
            ${order?.total ?? conversation.order_total ?? '0.00'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsCard;
